import React from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Table from 'src/components/UI/table/Table';
import {
  AppContext,
  LoadingContext,
  SnackContext
} from 'src/store/ContextStore';
import {
  useQuery,
  useQueryClient,
  keepPreviousData
} from '@tanstack/react-query';
import { capitalizeLetter, downloadFile } from 'src/util/util';
import CustomIconBtn from 'src/components/UI/button/CustomIconBtn';
import {
  toSearchText,
  toLabel,
  aggregateFamilyAnnotations
} from 'src/util/util';
import useDateFormatter from 'src/hooks/useDateFormatter';
import {
  APICONSTANTS,
  CONSTANTS,
  GLOBAL_CONSTANTS,
  TABLE_CONSTANTS
} from 'src/components/Constants';
import axios from 'src/axios';
import { EVENTS, displayableGenotypes } from 'src/components/admin/appReducer';
import {
  getTableStateFor,
  saveTableState,
  clampPageToTotal
} from 'src/components/UI/table/tableStatePersistence';
import {
  diagnosesMap,
  relationshipMap
} from 'src/components/questionnaire/Constants';
import { originWithBase } from 'src/util/basePath';
import { resolveTagStyle } from 'src/util/tagChipStyle';
import UserTableActions from './UserTableActions';
import UsersTableStatusCol from './UsersTableStatusCol';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { Visibility } from '@mui/icons-material';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import PersonSearchPicker from '../merge-person/PersonSearchPicker';
import MergePersonSelectModal from '../merge-person/MergePersonSelectModal';
import MergePersonWizard from '../merge-person/MergePersonWizard';

// Browse pagination is persisted under its own key, separate from the Table
// component's shared table state — see the note by the pagination state below.
const BROWSE_STATE_KEY = `${TABLE_CONSTANTS.familyMembers}:browse`;

export default function UsersTableInfo({
  openDeleteModal,
  patient,
  markAsImported,
  focusSearch
}) {
  const navigate = useNavigate();
  const fmtDate = useDateFormatter();
  // Ref to the wrapper's search input — replaces the old MRT-internals reach
  // (tableRef.current.refs.globalFilterInputRef) for the focusSearch feature.
  const searchInputRef = React.useRef(null);
  const { setLoading } = React.useContext(LoadingContext);
  const { setSnack } = React.useContext(SnackContext);
  const {
    content: { probandUsers: _proband_users },
    dispatchUpdate
  } = React.useContext(AppContext);

  const queryClient = useQueryClient();
  const highlightMatch = (label, query) => {
    if (!query) return label;
    const i = label.toLowerCase().indexOf(query);
    if (i === -1) return label;
    return (
      <>
        {label.slice(0, i)}
        <mark>{label.slice(i, i + query.length)}</mark>
        {label.slice(i + query.length)}
      </>
    );
  };

  const renderCollapsedTextWithQuery = (list, map, max = 2, queryVal) => {
    if (!Array.isArray(list) || list.length === 0) return null;

    const labels = list.map((it) => toLabel(it, map)).filter(Boolean);
    const q = (queryVal || '').trim().toLowerCase();

    let ordered = labels;
    if (q) {
      const matched = labels.filter((l) => l.toLowerCase().includes(q));
      const others = labels.filter((l) => !l.toLowerCase().includes(q));
      ordered = [...matched, ...others];
    }

    const shown = ordered.slice(0, max);
    const hasMore = ordered.length > max;

    return (
      <Typography
        variant="body2"
        noWrap
        title={labels.join(', ')}
        sx={{ display: 'block', maxWidth: '100%' }}
      >
        {shown.map((l, i) => (
          <React.Fragment key={`${l}-${i}`}>
            {i > 0 ? ', ' : ''}
            {highlightMatch(l, q)}
          </React.Fragment>
        ))}
        {hasMore ? ' …' : ''}
      </Typography>
    );
  };

  const [familyAgg, setFamilyAgg] = React.useState({});
  const loadingIdsRef = React.useRef(new Set());
  const familyAggRef = React.useRef({});

  const preloadFamilyAgg = React.useCallback(async (rows) => {
    const idsToFetch = (rows || [])
      .map((r) => r?._id)
      .filter(
        (id) =>
          id && !familyAggRef.current[id] && !loadingIdsRef.current.has(id)
      );

    if (idsToFetch.length === 0) return;

    idsToFetch.forEach((id) => loadingIdsRef.current.add(id));

    try {
      const results = await Promise.all(
        idsToFetch.map(async (id) => {
          try {
            const res = await axios.get(
              APICONSTANTS.getFamiliesInfo.replace(':user_id', id)
            );
            return { id, members: res.data || [] };
          } catch {
            return { id, members: [] };
          }
        })
      );

      setFamilyAgg((prev) => {
        const next = { ...prev };
        results.forEach(({ id, members }) => {
          next[id] = aggregateFamilyAnnotations(members);
          familyAggRef.current[id] = next[id];
        });
        return next;
      });
    } finally {
      idsToFetch.forEach((id) => loadingIdsRef.current.delete(id));
    }
  }, []);

  const famAggTexts = React.useMemo(() => {
    const out = {};
    for (const [id, agg] of Object.entries(familyAgg)) {
      out[id] = {
        diseases: toSearchText(agg?.diseases, diagnosesMap),
        phenotypes: toSearchText(agg?.phenotypes),
        genotypes: toSearchText(displayableGenotypes(agg?.genotypes))
      };
    }
    return out;
  }, [familyAgg]);

  const [globalFilter, setGlobalFilter] = React.useState('');

  // Server-paginated tables cannot use Table's own page persistence: it only
  // restores a saved page when the consumer passes no `page` prop, and a
  // controlled server table always passes one. So the page survived navigation
  // for every client-side table (Pedigrees et al) but never for this one.
  //
  // The parent owns the page here, so the parent owns restoring it. It also gets
  // its OWN key rather than sharing Table's: while a search is active this table
  // switches to client pagination with a hardcoded pageSize of 15, so the shared
  // key would hand browse mode a search-result page number and clobber a chosen
  // page size.
  const [pagination, setPagination] = React.useState(() => {
    const saved = getTableStateFor(BROWSE_STATE_KEY);
    return {
      pageIndex: saved?.page ?? 0,
      pageSize: saved?.pageSize ?? 15
    };
  });
  const searchActive = (globalFilter || '').trim().length > 0;

  // Persist only while browsing — search paging is transient and belongs to the
  // search results, not to the position we return the user to.
  React.useEffect(() => {
    if (searchActive) return;
    saveTableState(BROWSE_STATE_KEY, {
      page: pagination.pageIndex,
      pageSize: pagination.pageSize
    });
  }, [searchActive, pagination.pageIndex, pagination.pageSize]);

  const [enableIndexFetch, setEnableIndexFetch] = React.useState(false);
  React.useEffect(() => {
    if (!searchActive) {
      setEnableIndexFetch(false);
      return;
    }
    const id = setTimeout(() => setEnableIndexFetch(true), 500);
    return () => clearTimeout(id);
  }, [searchActive, globalFilter]);

  const {
    data: browseData,
    isLoading: browseLoading,
    isFetching: browseFetching,
    isError: browseError
  } = useQuery({
    queryKey: ['probandBrowse', pagination.pageIndex, pagination.pageSize],
    // React Query v5 REMOVED the `keepPreviousData: true` option — it was
    // silently ignored here, so every page change blanked `browseData` while the
    // next page loaded. That made `totalCount` fall to 0, the grid decided the
    // current page was out of range and emitted page 0, and the user was thrown
    // back to the first page mid-browse. The v5 spelling actually keeps the
    // previous page's rows AND its totalCount on screen during the fetch.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await axios.get(
        `${APICONSTANTS.getProbandBrowse}?page=${pagination.pageIndex}&limit=${pagination.pageSize}`
      );
      return res.data || { data: [], totalCount: 0 };
    }
  });

  const {
    data: indexData,
    isFetching: indexFetching,
    isError: indexError
  } = useQuery({
    queryKey: ['familySearchIndex'],
    enabled: enableIndexFetch,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const res = await axios.get(APICONSTANTS.getProbandSearchIndex);
      return res.data || [];
    }
  });

  // A page restored from a previous session can point past the end of the data.
  // Guarded on a real, non-zero totalCount so it can never fire during the
  // request-in-flight window — correcting the page there is the very bug that
  // was throwing the user back to page 1.
  React.useEffect(() => {
    if (searchActive) return;
    const totalCount = browseData?.totalCount ?? 0;
    const clamped = clampPageToTotal({
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      totalCount
    });
    if (clamped !== pagination.pageIndex) {
      setPagination((prev) => ({ ...prev, pageIndex: clamped }));
    }
  }, [browseData, pagination.pageIndex, pagination.pageSize, searchActive]);

  const browseRows = React.useMemo(() => browseData?.data ?? [], [browseData]);
  const indexRows = React.useMemo(() => indexData ?? [], [indexData]);
  const activeRows = searchActive ? indexRows : browseRows;

  // When AppContext signals a change (new record added), invalidate the cache.
  // Skip the initial mount so we don't bust the cache on every navigation.
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    queryClient.invalidateQueries({ queryKey: ['probandBrowse'] });
    queryClient.invalidateQueries({ queryKey: ['familySearchIndex'] });
  }, [_proband_users, queryClient]);

  React.useEffect(() => {
    setLoading(browseLoading);
  }, [browseLoading, setLoading]);

  React.useEffect(() => {
    if (browseError || indexError) {
      setSnack({
        status: true,
        msg: 'Error fetching values',
        severity: 'error'
      });
    }
  }, [browseError, indexError, setSnack]);

  const [mergeSourceRow, setMergeSourceRow] = React.useState(null);
  const [mergeSelectPersons, setMergeSelectPersons] = React.useState(null);
  const [mergeWizardPersons, setMergeWizardPersons] = React.useState(null);

  const hasFocused = React.useRef(false);
  React.useEffect(() => {
    if (!focusSearch || hasFocused.current || !activeRows.length) return;
    hasFocused.current = true;
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  }, [focusSearch, activeRows]);

  const onViewClick = (e, { user_id, _id }, isFamilyMember = false) => {
    e.stopPropagation();
    if (isFamilyMember) {
      navigate(
        CONSTANTS.familyMemberInfoPath
          .replace(':user_id', user_id)
          .replace(':member_id', _id)
      );
    } else navigate(`${CONSTANTS.probandViewPath}/${_id}`);
  };

  const onPedigreeViewClick = (e, rowData) => {
    e.stopPropagation();

    setLoading(true);
    // fetch family details for the selected row
    axios
      .get(APICONSTANTS.getFamiliesInfo.replace(':user_id', rowData._id))
      .then((res) => {
        setLoading(false);
        let value = { family: res.data, selectedUser: rowData };
        dispatchUpdate({ type: EVENTS.SET_PEDIGREE_SELECTED_USER, value });

        navigate(CONSTANTS.pedigreeChartPath.replace(':_id', rowData.familyId));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onFlatPedigreeViewClick = (e, rowData) => {
    e.stopPropagation();
    const probandId = rowData.user_id || rowData._id;
    setLoading(true);
    axios
      .get(APICONSTANTS.getFamiliesInfo.replace(':user_id', probandId))
      .then((res) => {
        setLoading(false);
        const value = { family: res.data, selectedUser: rowData };
        dispatchUpdate({ type: EVENTS.SET_PEDIGREE_SELECTED_USER, value });
        navigate({
          pathname: CONSTANTS.pedigreeChartPath.replace(
            ':_id',
            rowData.familyId
          ),
          state: { selectedMemberId: rowData._id }
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onViewPatientClick = (e, { _id }) => {
    e.stopPropagation();
    navigate(`${CONSTANTS.probandViewPath}/${_id}`);
  };

  // function when download xml btn is clicked
  const getXMLData = (e, { _id, familyId }) => {
    e.stopPropagation();
    axios
      .get(APICONSTANTS.getXMLData.replace(':user_id', _id))
      .then((res) => {
        downloadFile(res.data, familyId, 'xml');
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: 'Error exporting as xml data',
          severity: 'error'
        });
      });
  };

  const getJsonData = (e, { _id, familyId }) => {
    e.stopPropagation();
    axios
      .get(APICONSTANTS.getGA4JsonData.replace(':user_id', _id))
      .then((res) => {
        downloadFile(JSON.stringify(res.data), familyId, 'json');
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: 'Error converting to JSON',
          severity: 'error'
        });
      });
  };

  const createUniqueLink = (token) => {
    return `${originWithBase()}/questionnaire?${
      GLOBAL_CONSTANTS.tokenKey
    }=${token}`;
  };

  const patientView = (token) => {
    window.open(createUniqueLink(token), '_blank');
  };

  const t = useAutoTranslation();

  // @Dev : the below search funtion is not an appropiate method on seaching as the pedigree config may
  // not be a part of user model; also it may not have evey property of a member

  // const customSearch = (term, rowData) => {
  //   if (!rowData.firstName) rowData.firstName = '';
  //   const currentUser = probandUsers.data.filter((user) => {
  //     return user.currentId === rowData.currentId;
  //   })[0];
  //   let found = false;
  //   if (currentUser.pedigreeConfig?.length > 0) {
  //     for (let x in currentUser.pedigreeConfig) {
  //       let {
  //         firstName = '',
  //         lastName = '',
  //         comments = '',
  //         gender = '',
  //         relationship = ''
  //       } = currentUser.pedigreeConfig[x];
  //       term = term?.toLowerCase();
  //       if (firstName?.toLowerCase().includes(term)) found = true;
  //       if (lastName?.toLowerCase().includes(term)) found = true;
  //       if (comments?.toLowerCase().includes(term)) found = true;
  //       if (gender?.toLowerCase().includes(term)) found = true;
  //       if (relationship?.toLowerCase().includes(term)) found = true;
  //     }
  //   }
  //   return rowData.firstName?.toLowerCase().includes(term) || found;
  // };

  // eslint-disable-next-line no-unused-vars
  const columns = React.useMemo(
    () => [
      {
        accessorKey: 'familyId',
        header: t('Family ID')
      },
      {
        accessorKey: 'trakGeneId',
        header: t('TrakGene ID')
      },
      {
        accessorKey: 'firstName',
        header: t('First Name'),
        Cell: ({ cell }) => <span>{capitalizeLetter(cell.getValue())}</span>
      },
      {
        accessorKey: 'lastName',
        header: t('Last Name'),
        Cell: ({ cell }) => <span>{capitalizeLetter(cell.getValue())}</span>
      },
      {
        accessorKey: 'dob',
        header: t('Date of Birth'),
        Cell: ({ cell }) => <span>{fmtDate(cell.getValue())}</span>
      },
      {
        accessorKey: 'email',
        header: t('E-mail'),
        enableSorting: false
      },
      {
        id: 'familyDiseases',
        header: t('Family Diseases'),
        size: 220,
        enableSorting: false,
        accessorFn: (row) => famAggTexts[row._id]?.diseases || '',
        Cell: ({ row }) =>
          renderCollapsedTextWithQuery(
            familyAgg[row.original._id]?.diseases,
            diagnosesMap,
            2,
            globalFilter
          )
      },
      {
        id: 'familyPhenotypes',
        header: t('Family Phenotypes'),
        size: 220,
        enableSorting: false,
        accessorFn: (row) => famAggTexts[row._id]?.phenotypes || '',
        Cell: ({ row }) =>
          renderCollapsedTextWithQuery(
            familyAgg[row.original._id]?.phenotypes,
            undefined,
            2,
            globalFilter
          )
      },
      {
        id: 'familyGenotypes',
        header: t('Family Genotypes'),
        size: 220,
        enableSorting: false,
        accessorFn: (row) => famAggTexts[row._id]?.genotypes || '',
        Cell: ({ row }) =>
          renderCollapsedTextWithQuery(
            displayableGenotypes(familyAgg[row.original._id]?.genotypes),
            undefined,
            2,
            globalFilter
          )
      },
      {
        id: 'status',
        header: t('Status'),
        Cell: ({ row }) => (
          <UsersTableStatusCol
            status={row.original.status}
            submitted={row.original.submitted}
            merged={row.original.merged}
            mergedInto={row.original.mergedInto}
          />
        )
      },
      {
        id: 'actions',
        header: t('Actions'),
        enableSorting: false,
        size: 190,
        Cell: ({ row }) =>
          patient ? (
            <CustomIconBtn
              type="view"
              tooltip="View / Edit"
              onClick={() => patientView(row.original.token)}
            />
          ) : (
            <UserTableActions
              status={row.original.status}
              isProband={!!row.original.proBandId}
              onViewClick={(e) => onViewClick(e, row.original)}
              onPedigreeViewClick={(e) => onPedigreeViewClick(e, row.original)}
              onViewPatientClick={(e) => onViewPatientClick(e, row.original)}
              getXMLData={(e) => getXMLData(e, row.original)}
              onDeleteClick={(e) =>
                openDeleteModal(e, { ...row.original, tableData: row })
              }
              getJsonData={(e) => getJsonData(e, row.original)}
              markAsImported={(e) =>
                markAsImported(e, { ...row.original, tableData: row })
              }
              merged={row.original.merged}
            />
          ),
        muiTableBodyCellProps: !patient ? { sx: { px: 0 } } : {}
      }
    ],
    [t, famAggTexts, familyAgg, patient, globalFilter, fmtDate] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const boxColumns = React.useMemo(
    () => [
      {
        header: 'UPN',
        accessorKey: 'upn',
        size: 10,
        sortingFn: 'alphanumeric'
      },
      {
        header: t('Relationship'),
        accessorKey: 'relationship',
        Cell: ({ row }) => relationshipMap[row.original.relationship] ?? ''
      },
      {
        header: t('First Name'),
        accessorKey: 'firstName'
      },
      {
        header: t('Last Name'),
        accessorKey: 'lastName'
      },
      {
        header: t('Date of Birth'),
        accessorKey: 'dob',
        Cell: ({ row }) => fmtDate(row.original.dob)
      },
      {
        header: t('Date of Death'),
        accessorKey: 'dod',
        Cell: ({ row }) => fmtDate(row.original.dod)
      },
      {
        header: t('Disease (Age)'),
        id: 'diagnoses',
        accessorFn: (row) => {
          const list = row.diagnoses;
          if (!Array.isArray(list) || list.length === 0) return '';
          return list
            .map((it) => {
              const label = toLabel(it, diagnosesMap);
              const age =
                typeof it === 'string' ? row.diagnosesAge?.[it] : it?.age;
              return label + (age ? ` (${age})` : '');
            })
            .join(' | ');
        },
        enableSorting: false,
        size: 150,
        Cell: ({ row }) => {
          const list = row.original.diagnoses;
          const ageMap = row.original.diagnosesAge;
          if (!Array.isArray(list) || list.length === 0) return null;
          const q = (globalFilter || '').trim().toLowerCase();
          return list.map((it, i) => {
            const label = toLabel(it, diagnosesMap);
            const age = typeof it === 'string' ? ageMap?.[it] : it?.age;
            const text = label + (age ? ` (${age})` : '');
            return (
              <Typography variant="body2" key={i}>
                {highlightMatch(text, q)}
              </Typography>
            );
          });
        }
      },
      {
        header: t('Phenotypes'),
        id: 'phenotypes',
        accessorFn: (row) => {
          const list = row.phenotypes;
          if (!Array.isArray(list) || list.length === 0) return '';
          return list
            .map((p) => {
              const label = toLabel(p);
              const id = typeof p === 'object' ? p.id : '';
              return label + (id && label && id !== label ? ` (${id})` : '');
            })
            .join(' | ');
        },
        enableSorting: false,
        size: 180,
        Cell: ({ row }) => {
          const list = row.original.phenotypes;
          if (!Array.isArray(list) || list.length === 0) return null;
          const q = (globalFilter || '').trim().toLowerCase();
          return list.map((p, i) => {
            const label = toLabel(p);
            const id = typeof p === 'object' ? p.id : '';
            const text =
              label + (id && label && id !== label ? ` (${id})` : '');
            return (
              <Typography variant="body2" key={i}>
                {highlightMatch(text, q)}
              </Typography>
            );
          });
        }
      },
      {
        header: t('Genotypes'),
        id: 'genotypes',
        accessorFn: (row) => {
          const list = displayableGenotypes(row.genotypes);
          if (list.length === 0) return '';
          return list
            .map((g) => {
              const label = toLabel(g);
              const id = typeof g === 'object' ? g.id : '';
              return label + (id && label && id !== label ? ` (${id})` : '');
            })
            .join(' | ');
        },
        enableSorting: false,
        size: 180,
        Cell: ({ row }) => {
          const list = displayableGenotypes(row.original.genotypes);
          if (list.length === 0) return null;
          const q = (globalFilter || '').trim().toLowerCase();
          return list.map((g, i) => {
            const label = toLabel(g);
            const id = typeof g === 'object' ? g.id : '';
            const text =
              label + (id && label && id !== label ? ` (${id})` : '');
            return (
              <Typography variant="body2" key={i}>
                {highlightMatch(text, q)}
              </Typography>
            );
          });
        }
      },
      {
        header: t('Tags'),
        id: 'personTags',
        accessorFn: (row) =>
          (row.personTags || []).map(({ tag }) => tag).join(' '),
        enableSorting: false,
        size: 200,
        Cell: ({ row }) => {
          const tags = row.original.personTags;
          if (!Array.isArray(tags) || tags.length === 0) return null;
          return (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {tags.map(({ tag, tagColor }) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  style={resolveTagStyle(tag, tagColor)}
                  sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                />
              ))}
            </div>
          );
        },
        filterFn: (row, id, filterValue) => {
          const tags = row.original.personTags ?? [];
          const tagsText = tags.map(({ tag }) => tag?.toLowerCase()).join(' ');
          return tagsText.includes(filterValue.toLowerCase());
        }
      },
      {
        header: t('Comments'),
        accessorKey: 'comments',
        enableSorting: false
      },
      {
        header: t('Actions'),
        accessorKey: 'actions',
        enableSorting: false,
        size: 80,
        Cell: ({ row }) => {
          const rowData = row.original;
          return (
            <>
              {rowData.disableEdit ? (
                <CustomIconBtn
                  icon={<Visibility fontSize="small" />}
                  disabled={true}
                />
              ) : (
                <CustomIconBtn
                  type="view"
                  tooltip="View Submission"
                  onClick={(e) => onViewClick(e, rowData, true)}
                  disabled={rowData.disableEdit}
                />
              )}
            </>
          );
        }
      }
    ],
    [t, globalFilter, fmtDate] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const flatColumns = React.useMemo(() => {
    const familyIdCol = {
      accessorKey: 'familyId',
      header: t('Family ID'),
      size: 120
    };
    const memberCols = boxColumns.filter((c) => c.accessorKey !== 'actions');
    const statusCol = {
      id: 'status',
      header: t('Status'),
      enableSorting: false,
      Cell: ({ row }) => (
        <UsersTableStatusCol
          status={row.original.status}
          merged={row.original.merged}
          mergedInto={row.original.mergedInto}
        />
      )
    };
    const actionCol = patient
      ? []
      : [
          {
            header: t('Actions'),
            accessorKey: 'actions',
            enableSorting: false,
            size: 150,
            Cell: ({ row }) => {
              const rowData = row.original;
              const isProband = rowData.upn === 1;
              return (
                <>
                  {rowData.disableEdit ? (
                    <CustomIconBtn
                      icon={<Visibility fontSize="small" />}
                      disabled={true}
                    />
                  ) : (
                    <CustomIconBtn
                      type="view"
                      tooltip="View Submission"
                      onClick={(e) => onViewClick(e, rowData, !isProband)}
                    />
                  )}
                  <CustomIconBtn
                    type="view-pedigree"
                    tooltip="View Pedigree"
                    onClick={(e) => onFlatPedigreeViewClick(e, rowData)}
                  />
                  {false && !rowData.merged && (
                    <CustomIconBtn
                      icon={<MergeTypeIcon fontSize="small" />}
                      tooltip="Merge Person"
                      onClick={() =>
                        setMergeSourceRow({
                          firstName: rowData.firstName,
                          lastName: rowData.lastName,
                          dob: rowData.dob,
                          familyId: rowData.familyId,
                          // Probands carry proBandId; family members carry familyMemberId.
                          familyMemberId:
                            rowData.familyMemberId || rowData.proBandId
                        })
                      }
                    />
                  )}
                </>
              );
            }
          }
        ];
    return [familyIdCol, ...memberCols, statusCol, ...actionCol];
  }, [t, boxColumns, patient]); // eslint-disable-line react-hooks/exhaustive-deps

  const probandTable = React.useMemo(() => {
    const paginationProps = searchActive
      ? {
          pageSize: 15,
          page: 0,
          state: { isLoading: indexFetching || !indexData }
        }
      : {
          pageSize: pagination.pageSize,
          page: pagination.pageIndex,
          totalCount: browseData?.totalCount ?? 0,
          // Table (DataGrid) hands back a resolved { page, pageSize } model;
          // map it onto our internal { pageIndex, pageSize } shape.
          onPaginationChange: (model) =>
            setPagination({
              pageIndex: model.page,
              pageSize: model.pageSize
            }),
          // isFetching, not isLoading: with placeholderData the previous page
          // stays on screen and isLoading is false from the second page onward,
          // so isLoading alone would show no feedback at all while paging.
          state: { isLoading: browseFetching }
        };

    return (
      <Table
        title="Family Members"
        rows={activeRows}
        pageSizeOptions={[5, 10, 15, 20, 30, 40, 50]}
        columns={flatColumns}
        dense={true}
        searchInputRef={searchInputRef}
        tableKey={TABLE_CONSTANTS.familyMembers}
        columnPinning={{ right: ['actions'] }}
        globalFilter={globalFilter}
        onGlobalFilterChange={(v) => setGlobalFilter(v ?? '')}
        {...paginationProps}
      />
    );
  }, [
    searchActive,
    activeRows,
    flatColumns,
    focusSearch,
    globalFilter,
    pagination,
    browseData,
    browseLoading,
    browseFetching,
    indexFetching,
    indexData
  ]);

  return (
    <>
      {probandTable}
      {mergeSourceRow && (
        <PersonSearchPicker
          open={!!mergeSourceRow}
          onClose={() => setMergeSourceRow(null)}
          onSelect={(selectedPerson) => {
            setMergeSelectPersons({
              personA: mergeSourceRow,
              personB: selectedPerson
            });
            setMergeSourceRow(null);
          }}
          excludeFamilyMemberId={mergeSourceRow.familyMemberId}
        />
      )}
      {mergeSelectPersons && (
        <MergePersonSelectModal
          open={!!mergeSelectPersons}
          personA={mergeSelectPersons.personA}
          personB={mergeSelectPersons.personB}
          onClose={() => setMergeSelectPersons(null)}
          onSelect={({ retained, deleted }) => {
            setMergeSelectPersons(null);
            setMergeWizardPersons({ retained, deleted });
          }}
        />
      )}
      {mergeWizardPersons && (
        <MergePersonWizard
          open={!!mergeWizardPersons}
          retained={mergeWizardPersons.retained}
          deleted={mergeWizardPersons.deleted}
          onClose={() => setMergeWizardPersons(null)}
          onSuccess={() => {
            setMergeWizardPersons(null);
            queryClient.invalidateQueries({ queryKey: ['probandBrowse'] });
            queryClient.invalidateQueries({ queryKey: ['familySearchIndex'] });
          }}
        />
      )}
    </>
  );
}
